import { Component, OnDestroy, OnInit, Pipe, PipeTransform } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { EMPTY, Observable, Subject, from, of } from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { ConcentrationsModalComponent } from '../../core/modals/concentrations/concentrations.component';
import { AuthenticationService } from '../../core/services/authentication.service';
import { LogCategory, LoggerService, LogPage, LogType } from '../../core/services/logger.service';
import { ProfileService } from '../../core/services/profile.service';
import { TagManagerService } from '../../core/services/tag-manager.service';
import { PeopleService } from '../../services/people.service';
//Teams Integration 2021
import { ActivatedRoute } from '@angular/router';
import { AppComponent } from '../../app.component';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false
})
export class ProfileComponent implements OnInit, OnDestroy {

    private fileSelected$ = new Subject<Event>();
    //  Consumed by the image cropper via | async in the template; emits the raw file-change Event on a valid JPG, or '' to reset.
    public imageChangedEvent$: Observable<Event | ''> = this.fileSelected$.pipe(
        tap(() => this.uploadError = ''),
        switchMap((event) => {
            const input = event.target as HTMLInputElement;
            const file = input.files?.[0];
            if (!file) return EMPTY;
            return this.isJpeg(file).pipe(
                map((isJpeg) => isJpeg ? event : this.rejectNonJpeg(input))
            );
        }),
        startWith('' as const)
    );
    private croppedImage: any = '';
    private cropperReady = false;
    public uploadError: string = '';

    public isSaved: boolean = false;
    public isAudienceRunning: boolean = false;
    public isRevertAudienceRunning: boolean = false;
    public isSpoofRunning: boolean = false;
    public isExpanded: boolean[] = [false];

    public isHistoryExpanded = false;
    private hasTableHeaders: boolean = false;  // needed for accessibility fixes for google chart


    public searchResults$: Observable<any> = null;
    public selectedPerson: any = undefined;

    constructor(
        public profileService: ProfileService,
        public auth: AuthenticationService,
        //Teams Intergration 2021
        public app: AppComponent,
        private route: ActivatedRoute, 
        private logger: LoggerService,
        public gtm: TagManagerService,
        private modalService: NgbModal,
        public peopleService: PeopleService
    ) {
        this.log("constructor", null, LogCategory.Constructor);
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Page, LogPage.Profile, category);
    }

    public onProfileSubmit(data, makePrivate: boolean = false) {
        this.log("onProfileSubmit", "Profile submitted");

        for (let i = 0; i < data.length; i++) {
            data[i].IsPublished = !makePrivate; 
        }

        this.profileService.updateSettings(data);
        this.isSaved = true; 
    }

    ngOnInit() {
        this.log("ngOnInit", null, LogCategory.Init);
    }

    ngOnDestroy() {
        this.log("ngOnDestroy", null, LogCategory.Destroy);
    }

    public fileChangeEvent(event: Event): void {
        this.fileSelected$.next(event);
    }

    //  MIME check catches most cases; magic-bytes check (FF D8 FF prefix) catches renamed non-JPEG files.
    private isJpeg(file: File): Observable<boolean> {
        if (file.type !== 'image/jpeg') return of(false);
        return from(file.slice(0, 3).arrayBuffer()).pipe(
            map(buf => {
                const bytes = new Uint8Array(buf);
                return bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF;
            }),
            catchError(() => of(false))
        );
    }
    private rejectNonJpeg(input: HTMLInputElement): '' {
        this.uploadError = 'Only JPG files are allowed. Please choose a .jpg image.';
        input.value = '';
        return '';
    }
    public imageCropped(event: ImageCroppedEvent) {
        this.log("imageCropped", "Profile photo cropped.");
        this.croppedImage = event.base64;
    }
    public imageLoaded() {
        this.cropperReady = true;
    }
    public loadImageFailed() {
        this.uploadError = 'We couldn\'t load that image. Please try a different JPG.';
        this.log("loadImageFailed", "Failed to load the image.");
    }
    public saveCustomProfilePhoto() {
        this.log("saveCustomProfilePhoto", "Saving custom profile image.");
        this.profileService.updatePhoto(this.croppedImage);
    }
    public deleteCustomProfilePhoto() {
        this.log("deleteCustomProfilePhoto", "Deleting custom profile image.");
        this.profileService.deletePhoto();
    }

    private saveWidget(widgetMeta: any) {
        this.profileService.updateWidgetPreference(widgetMeta);
    }

    public toggleFavorite(widgetMeta: any) {        
        widgetMeta.IsFave = !widgetMeta.IsFave;
        widgetMeta.SizeAsFave = 12 //full width
        this.saveWidget(widgetMeta);
    }

    public selectTheme(theme: any) {
        var data: ProfileSetting[] = [new ProfileSetting()]

        data[0].Id = ".Myke.Theme";
        data[0].Type = "text";
        data[0].Value = theme;
        this.profileService.updateSettings( data );
    }

    //public canViewThemes(): boolean {
    //    return this.auth.hasFeature('Themes');
    //}

    //  Clears person service variable and performs person lookup if minimum characters are met
    public triggerSearch(searchString) {
        switch (true) {
            case searchString.length >= 2:
                this.searchResults$ = this.peopleService.people;  //  API call for people
                break;
            case searchString.length == 0:
                this.peopleService.typeaheadQuery = undefined;
            default:
                this.searchResults$ = null;
                this.peopleService.typeaheadQuery = undefined;
                break;
        }
    }

    //  Selects a person from the typeahead results dropdown
    public selectPerson(person) {
        this.searchResults$ = null;
        this.peopleService.typeaheadQuery = person.DisplayNameInternal;
        this.selectedPerson = person;
    }

    public openConc(concentrations) {
        this.isHistoryExpanded = false;
        this.profileService.refetchConcentrationData();
        const modalRef = this.modalService.open(ConcentrationsModalComponent, { ariaLabelledBy: 'modal-basic-title' });
        modalRef.componentInstance.type = ( concentrations && concentrations.length > 0) ? concentrations[0].Type : '';
        modalRef.componentInstance.numConc = concentrations ? concentrations.length : 0;
        modalRef.result.then((result) => { // If the modal was closed with save, then update the concentrations                 
                this.profileService.saveConcentration(result);
            },
                (result) => {}); // otherwise, ignore the result 
    }
}

export class ProfileSetting {
    Id: string;
    Type: string;
    Value: string;
}

@Pipe({
    name: 'sortSettings',
    standalone: false
})

export class SortPipe implements PipeTransform {
    transform(items: any[]): any[] {
       if (!items) return [];
       
      return Object.entries(items)
            .sort(([, a], [, b]) => {
                return (a.toLowerCase() < b.toLowerCase()) ? -1 : (a.toLowerCase() > b.toLowerCase()) ? 1 : 0
            })
            .map(([v, k]) => ({ key: v, value: k }))              
    }
}
